import React, { useState } from "react";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import { Alert, Button, Card, CardHeader, Checkbox, DatePicker, Select, TextInput } from "@opendate-app/setlist";
import client from "../../shared/api/client";
import { toastError, toastSuccess } from "../../shared/toastHelper";
import StaffingSettingsLayout from "./components/StaffingSettingsLayout";
import { staffingTabPathsShape } from "./components/StaffingTabs";
import LaborRulesCard from "./LaborRulesCard";
import ToastLaborSyncBanners from "./ToastLaborSyncBanners";
import KioskDevicesCard from "./KioskDevicesCard";

const parseDate = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (date) => {
  if (!date) return null;
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const StaffingSettingsApp = ({
  settings,
  updatePath,
  frequencyOptions,
  weekdayOptions,
  reminderMinutesMaximum,
  lookbackWeeksMaximum,
  availabilityDayMaximum,
  tabPaths,
  laborRules,
  toastLaborSyncs,
  kioskDevices,
}) => {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const save = async () => {
    setSaving(true);
    setErrors([]);
    try {
      const { data } = await client.patch(updatePath, { staffing_setting: form });
      setForm(data);
      toastSuccess("Staffing settings saved.");
    } catch (error) {
      const messages = error?.response?.data?.errors || [];
      setErrors(messages);
      toastError("Could not save staffing settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <StaffingSettingsLayout
      current="general"
      title="General"
      description="How pay periods, shifts and scheduling work for your team."
      tabPaths={tabPaths}
      maxWidth={960}
      actions={
        <Button variant="primary" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save settings"}
        </Button>
      }
    >
      {errors.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Alert color="error" title="Could not save staffing settings" description={errors.join(" ")} />
        </div>
      )}

      <ToastLaborSyncBanners toastLaborSyncs={toastLaborSyncs} />

      <div>
        <Card
          padding="md"
          style={{ marginBottom: 20 }}
          header={<CardHeader title="Pay periods" supportingText="Timesheets and payroll exports are grouped by pay period." />}
        >
          <div style={{ marginBottom: 16 }}>
            <Select
              id="staffing-pay-period-frequency"
              label="Pay period"
              options={frequencyOptions}
              value={form.pay_period_frequency}
              onChange={(value) => setField("pay_period_frequency", value)}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <DatePicker
              id="staffing-pay-period-anchor"
              label="Pay period start date"
              hint="The first day of any pay period. Earlier and later periods count from this date."
              value={parseDate(form.pay_period_anchor_on)}
              onChange={(date) => setField("pay_period_anchor_on", formatDate(date))}
            />
          </div>
          <Select
            id="staffing-workweek-start"
            label="Workweek starts on"
            hint="Weekly overtime is counted from this day."
            options={weekdayOptions}
            value={form.workweek_start_wday}
            onChange={(value) => setField("workweek_start_wday", Number(value))}
          />
        </Card>

        <Card
          padding="md"
          style={{ marginBottom: 20 }}
          header={<CardHeader title="Shifts" supportingText="Reminders and approvals for scheduled shifts." />}
        >
          <div style={{ marginBottom: 16, maxWidth: 280 }}>
            <TextInput
              id="staffing-reminder-minutes"
              label="Shift reminder"
              type="number"
              min={0}
              max={reminderMinutesMaximum}
              trailingText="minutes before"
              value={form.reminder_minutes_before ?? ""}
              onChange={(event) => setField("reminder_minutes_before", event.target.value)}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <Checkbox
              id="staffing-open-shift-pickup"
              label="Staff can pick up open shifts without approval"
              checked={form.allow_open_shift_pickup_without_approval}
              onChange={(event) => setField("allow_open_shift_pickup_without_approval", event.target.checked)}
            />
          </div>
          <Checkbox
            id="staffing-department-approval"
            label="Department leads must approve their department's hours before a pay period can be locked"
            checked={form.require_department_approval}
            onChange={(event) => setField("require_department_approval", event.target.checked)}
          />
        </Card>

        <Card
          padding="md"
          style={{ marginBottom: 20 }}
          header={<CardHeader title="Smart Schedule" supportingText="Suggested schedules are based on recent shifts." />}
        >
          <div style={{ maxWidth: 280 }}>
            <TextInput
              id="staffing-lookback-weeks"
              label="Look back"
              type="number"
              min={1}
              max={lookbackWeeksMaximum}
              trailingText="weeks"
              value={form.smart_schedule_lookback_weeks ?? ""}
              onChange={(event) => setField("smart_schedule_lookback_weeks", event.target.value)}
            />
          </div>
        </Card>

        <Card
          padding="md"
          style={{ marginBottom: 20 }}
          header={
            <CardHeader
              title="Availability"
              supportingText="Each month's availability opens and closes on these days of the month before. Next month is set up on the 1st, so a change made after that starts with the month after."
            />
          }
        >
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <div style={{ width: 200 }}>
              <TextInput
                id="staffing-availability-opens-day"
                label="Opens on day"
                type="number"
                min={1}
                max={availabilityDayMaximum}
                value={form.availability_opens_day ?? ""}
                onChange={(event) => setField("availability_opens_day", event.target.value)}
              />
            </div>
            <div style={{ width: 200 }}>
              <TextInput
                id="staffing-availability-closes-day"
                label="Closes after day"
                type="number"
                min={1}
                max={availabilityDayMaximum}
                value={form.availability_closes_day ?? ""}
                onChange={(event) => setField("availability_closes_day", event.target.value)}
              />
            </div>
          </div>
        </Card>

        <LaborRulesCard laborRules={laborRules} />

        <KioskDevicesCard kioskDevices={kioskDevices} />
      </div>

      <ToastContainer icon={false} />
    </StaffingSettingsLayout>
  );
};

const optionShape = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
});

StaffingSettingsApp.propTypes = {
  settings: PropTypes.shape({
    pay_period_frequency: PropTypes.string,
    pay_period_anchor_on: PropTypes.string,
    workweek_start_wday: PropTypes.number,
    reminder_minutes_before: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    allow_open_shift_pickup_without_approval: PropTypes.bool,
    require_department_approval: PropTypes.bool,
    smart_schedule_lookback_weeks: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    availability_opens_day: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    availability_closes_day: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  updatePath: PropTypes.string.isRequired,
  frequencyOptions: PropTypes.arrayOf(optionShape).isRequired,
  weekdayOptions: PropTypes.arrayOf(optionShape).isRequired,
  reminderMinutesMaximum: PropTypes.number.isRequired,
  lookbackWeeksMaximum: PropTypes.number.isRequired,
  availabilityDayMaximum: PropTypes.number.isRequired,
  tabPaths: staffingTabPathsShape.isRequired,
  laborRules: PropTypes.object.isRequired,
  toastLaborSyncs: PropTypes.array.isRequired,
  kioskDevices: PropTypes.array.isRequired,
};

export default StaffingSettingsApp;
